// deps
import React, { memo, useCallback, useMemo } from "react";

// internal deps
import { RenderingDetails } from "@swiggy-private/cart-types";
import { filterRenderingDetails } from "@cart-client/helpers/CartRendering";
import { recordCBCCInterest } from "@cart-client/apis/Cart";
import {
    CartCutleryInstruction,
    CbccNudge,
    DelfeeDiscount,
    OfferPackNudgeWidget,
    OTHER_CART_ITEM_TYPES,
    TransparentLine,
    TransparentLineV2,
    VALID_RENDERING_DETAILS_TYPE,
} from "@swiggy-private/cart-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// hooks
import { useCbccWidgetData } from "@cart-client/hooks/useCbccWidgetData";
import { useOfferPackWidgetData } from "@cart-client/hooks/useOfferPackWidgetData";
import { useCutleryInstructionData } from "@cart-client/hooks/useCutleryInstructionData";

export interface IProps {
    renderingDetails: RenderingDetails[];
    setCroutonVisibility: (croutonVisibility: boolean) => void;
}

const CartOtherItemsRenderer = ({ renderingDetails, setCroutonVisibility }: IProps) => {
    const otherItemsRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, OTHER_CART_ITEM_TYPES),
        [renderingDetails],
    );

    const cbccNudgeData = useCbccWidgetData();
    const offerPackData = useOfferPackWidgetData(renderingDetails);
    const { cutleryWidgetData, showCutleryInstruction } = useCutleryInstructionData();

    const renderCartItem = useCallback(
        (itemData: RenderingDetails) => {
            switch (itemData.type.toUpperCase()) {
                case VALID_RENDERING_DETAILS_TYPE.DELIVERY_FEE_DISCOUNT:
                    return (
                        <>
                            <TransparentLine />
                            <DelfeeDiscount itemData={itemData} />
                        </>
                    );

                case VALID_RENDERING_DETAILS_TYPE.CART_CBCC_NUDGE:
                    if (!isEmpty(itemData?.meta?.banners?.[0])) {
                        return (
                            <>
                                <TransparentLine />
                                <CbccNudge
                                    setCroutonVisibility={setCroutonVisibility}
                                    recordCbccInterest={recordCBCCInterest}
                                    itemData={itemData}
                                    {...cbccNudgeData}
                                />
                            </>
                        );
                    }
                    return null;

                case VALID_RENDERING_DETAILS_TYPE.OFFER_BUNDLE_BENEFITS:
                case VALID_RENDERING_DETAILS_TYPE.OFFER_BUNDLE_NUDGE:
                    return (
                        <>
                            <TransparentLine />
                            <OfferPackNudgeWidget item={itemData} {...offerPackData} />
                        </>
                    );

                default:
                    return null;
            }
        },
        [cbccNudgeData, offerPackData, setCroutonVisibility],
    );

    return (
        <>
            {showCutleryInstruction && (
                <>
                    <TransparentLineV2 />
                    <CartCutleryInstruction {...cutleryWidgetData} />
                </>
            )}
            {otherItemsRenderingDetails.map((renderingDetail) => renderCartItem(renderingDetail))}
        </>
    );
};

export default memo(CartOtherItemsRenderer);
